import { pushApi } from '../bot/api'

/**
 * Web push, browser side, for any feature: this browser subscribes once through the service
 * worker in public/push-sw.js, and from then on server-ai can notify it whether the app is
 * open or not. What a feature sends is up to the server (Logic/PushNotification.js).
 */

export const PUSH_SW_URL = '/push-sw.js'

export interface WebPushStatus {
  /** The browser can do push, has not blocked the site, and the server has a key. */
  available: boolean
  /** This browser is subscribed and the server has it on file. */
  subscribed: boolean
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

async function currentSubscription() {
  const registration = await navigator.serviceWorker.getRegistration(PUSH_SW_URL)
  return (await registration?.pushManager.getSubscription()) ?? null
}

export async function webPushStatus(): Promise<WebPushStatus> {
  if (!pushSupported() || Notification.permission === 'denied') return { available: false, subscribed: false }
  const subscription = await currentSubscription()
  const { publicKey, subscribed } = await pushApi.status(subscription?.endpoint)
  return {
    available: Boolean(publicKey),
    subscribed: Boolean(publicKey && subscription && subscribed && Notification.permission === 'granted'),
  }
}

/**
 * Asks the browser's own permission, then subscribes this browser and hands the subscription to
 * the server. False when the trader blocks or dismisses the prompt, or push is unavailable.
 */
export async function subscribeToPush(): Promise<boolean> {
  if (!pushSupported()) return false
  if ((await Notification.requestPermission()) !== 'granted') return false

  const { publicKey } = await pushApi.status()
  if (!publicKey) return false

  const registration = await navigator.serviceWorker.register(PUSH_SW_URL)
  await navigator.serviceWorker.ready
  const applicationServerKey = base64UrlToBytes(publicKey)

  let subscription = await registration.pushManager.getSubscription()
  if (subscription && !sameKey(subscription, applicationServerKey)) {
    // Made under an older server key: the push service would refuse every send to it.
    await subscription.unsubscribe()
    subscription = null
  }
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })

  await pushApi.subscribe(subscription.toJSON())
  return true
}

/** Routes the app when a notification click lands on an already open tab (push-sw.js). */
export function onPushNavigate(navigate: (path: string) => void) {
  if (!pushSupported()) return () => {}
  const listener = (event: MessageEvent) => {
    if (event.data?.type === 'algobuilder:navigate' && typeof event.data.path === 'string') navigate(event.data.path)
  }
  navigator.serviceWorker.addEventListener('message', listener)
  return () => navigator.serviceWorker.removeEventListener('message', listener)
}

/**
 * Tells the service worker which page this tab shows when a push arrives (push-sw.js). The
 * worker's own view of a tab's URL stays on the page it was loaded on, so after the app
 * routes to another strategy it would think the trader is still looking at the old one.
 */
export function answerPushLocation() {
  if (!pushSupported()) return () => {}
  const listener = (event: MessageEvent) => {
    if (event.data?.type === 'algobuilder:where') event.ports[0]?.postMessage({ path: window.location.pathname })
  }
  navigator.serviceWorker.addEventListener('message', listener)
  return () => navigator.serviceWorker.removeEventListener('message', listener)
}

function base64UrlToBytes(value: string) {
  const base64 = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/')
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
}

function sameKey(subscription: PushSubscription, key: Uint8Array) {
  const current = subscription.options?.applicationServerKey
  if (!current) return true
  const bytes = new Uint8Array(current)
  return bytes.length === key.length && bytes.every((b, i) => b === key[i])
}
